import {
  AUTH_OPT_IN_HEADERS,
  createSessionManager,
  sessionFromResponse,
} from "./authSession";

// The signed-in session in localStorage, shared with mobile-next's web export
// (same origin, same layout):
//
// - `token`: the access token, or a legacy single token. Everything that reads
//   `localStorage.getItem("token")` keeps working.
// - `authSession`: JSON `{ accessToken, refreshToken, accessTokenExpiresAt,
//   refreshTokenExpiresAt }`, only for a session with a refresh token. It
//   counts only while its `accessToken` matches `token`, so a legacy token
//   written over it wins.
const TOKEN_KEY = "token";
const SESSION_KEY = "authSession";

/** How long sign-out waits for a token refresh before revoking with the old one. */
const LOGOUT_TOKEN_TIMEOUT_MS = 2000;

function readSession() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  try {
    const session = JSON.parse(localStorage.getItem(SESSION_KEY));
    if (
      session?.accessToken === token &&
      typeof session.refreshToken === "string"
    ) {
      return {
        accessToken: token,
        refreshToken: session.refreshToken,
        accessTokenExpiresAt:
          typeof session.accessTokenExpiresAt === "number"
            ? session.accessTokenExpiresAt
            : null,
        refreshTokenExpiresAt:
          typeof session.refreshTokenExpiresAt === "number"
            ? session.refreshTokenExpiresAt
            : null,
      };
    }
  } catch {
    // Unreadable: treat the token as a legacy one.
  }

  return {
    accessToken: token,
    refreshToken: null,
    accessTokenExpiresAt: null,
    refreshTokenExpiresAt: null,
  };
}

function writeSession(session) {
  if (session.refreshToken) {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
  localStorage.setItem(TOKEN_KEY, session.accessToken);
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
}

async function requestRefresh(refreshToken) {
  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...AUTH_OPT_IN_HEADERS },
    body: JSON.stringify({ refreshToken }),
  });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(data?.message ?? "Refresh failed");
    error.status = response.status;
    error.code = data?.code;
    throw error;
  }
  return data;
}

// Web Locks keep two tabs (or this app and mobile-next's web export) from
// spending the same refresh token at once.
function withRefreshLock(fn) {
  if (!navigator.locks) return fn();
  return navigator.locks.request("congrs-auth-refresh", fn);
}

// React Query lives below the router, so the redirect is a hard one.
const redirectToLogin = () => {
  window.location.href = "/login";
};

const manager = createSessionManager({
  storage: {
    read: async () => readSession(),
    write: async (session) => writeSession(session),
    clear: async () => clearSession(),
  },
  requestRefresh,
  onSessionEnded: redirectToLogin,
  withLock: withRefreshLock,
});

/** What a 401 on a request sent with `token` means. See `authSession.js`. */
export const handleUnauthorized = manager.handleUnauthorized;

/**
 * The bearer token to send: the stored access token, refreshed first when it
 * is about to expire. `null` when signed out.
 */
export const getAccessToken = manager.getAccessToken;

/**
 * The token to send for a request a caller read `token` for: the fresh access
 * token when that is the stored one, the caller's own otherwise.
 */
export async function freshToken(token) {
  if (!token || token !== localStorage.getItem(TOKEN_KEY)) return token;
  return (await getAccessToken()) ?? token;
}

/**
 * Store the session a sign-in endpoint answered with, given its body as is:
 * legacy `{ user, token }` or the opted-in pair. Returns the access token.
 */
export function applySignInResponse(response) {
  const session = sessionFromResponse(response, Date.now());
  manager.invalidate();
  writeSession(session);
  return session.accessToken;
}

/** Forget the session locally, e.g. after the account was deleted. */
export function clearAuthSession() {
  manager.invalidate();
  clearSession();
}

/**
 * Sign out on purpose: revoke the session on the API (best effort, ignored by
 * an API without sessions), then forget it locally. `keepalive` lets the
 * request finish when the caller navigates away right after.
 */
export async function logout() {
  const stored = readSession()?.accessToken ?? null;
  const token = stored
    ? await Promise.race([
        getAccessToken(),
        new Promise((resolve) =>
          setTimeout(() => resolve(stored), LOGOUT_TOKEN_TIMEOUT_MS)
        ),
      ])
    : null;

  // Read after the refresh above, which may have rotated it. Sent along so
  // the session is revoked even when the access token expired and that
  // refresh did not get through.
  const refreshToken = readSession()?.refreshToken ?? null;

  if (token) {
    fetch("/api/auth/logout", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(refreshToken ? { "Content-Type": "application/json" } : {}),
      },
      body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
      keepalive: true,
    }).catch(() => {});
  }

  clearAuthSession();
}
